import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Search, MessageCircle, User } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Modal "Abrir conversa Kommo" — lista pesquisável de todos os clientes que
 * têm kommoLeadId. Clique abre o Kommo directamente numa nova aba para enviar
 * mensagem rapidamente.
 */
export default function AbrirKommoModal({ onClose }) {
    const [vendas, setVendas] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/vendas")
            .then((r) => {
                const list = (Array.isArray(r.data) ? r.data : [])
                    .filter((v) => v.kommoLeadId && !v.cancelada);
                // Ordenar: com telemóvel primeiro, depois por data mais recente
                list.sort((a, b) => String(b.dataVenda || "").localeCompare(String(a.dataVenda || "")));
                setVendas(list);
            })
            .catch(() => setVendas([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        if (q.length < 2) return vendas.slice(0, 30);
        const s = q.toLowerCase().trim();
        return vendas.filter((v) =>
            (v.nome || "").toLowerCase().includes(s) ||
            String(v.telefone || "").includes(s) ||
            String(v.nif || "").includes(s) ||
            (v.localidade || v.cidade || "").toLowerCase().includes(s)
        ).slice(0, 30);
    }, [vendas, q]);

    const openKommo = (v) => {
        window.open(`https://salvadordomeo.kommo.com/leads/detail/${v.kommoLeadId}`, "_blank");
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} onClick={onClose}>
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", damping: 25, stiffness: 260 }}
                className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                style={{ background: "var(--v2-surface)", borderColor: "var(--v2-border)", color: "var(--v2-foreground)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="modal-kommo"
            >
                <div className="h-12 px-4 flex items-center border-b" style={{ borderColor: "var(--v2-border)" }}>
                    <div className="w-6 h-6 rounded-md flex items-center justify-center text-white font-bold text-[11px]" style={{ background: "var(--v2-primary)" }}>K</div>
                    <span className="ml-2 font-headings font-semibold">Abrir conversa Kommo</span>
                    <button onClick={onClose} className="ml-auto h-7 w-7 rounded-md flex items-center justify-center hover:bg-[color:var(--v2-surface-hover)]"><X size={15} /></button>
                </div>

                <div className="p-3 border-b" style={{ borderColor: "var(--v2-border)" }}>
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-2.5" style={{ color: "var(--v2-muted)" }} />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, telefone, NIF ou cidade…"
                            className="w-full h-8 pl-8 pr-2 rounded-md border bg-transparent text-[13px] outline-none focus:border-[color:var(--v2-primary)]"
                            style={{ borderColor: "var(--v2-border)" }}
                            data-testid="kommo-search"
                        />
                    </div>
                    <div className="text-[10px] mt-1.5" style={{ color: "var(--v2-muted)" }}>
                        {vendas.length} clientes sincronizados com Kommo · toca para abrir conversa
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-[12px]" style={{ color: "var(--v2-muted)" }}>A carregar…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-[12px]" style={{ color: "var(--v2-muted)" }}>Nenhum cliente encontrado.</div>
                    ) : (
                        filtered.map((v) => (
                            <button
                                key={v.id}
                                onClick={() => openKommo(v)}
                                className="w-full flex items-center gap-3 h-14 px-3 hover:bg-[color:var(--v2-surface-hover)] text-left border-b transition-colors"
                                style={{ borderColor: "var(--v2-border)" }}
                                data-testid={`kommo-row-${v.id}`}
                            >
                                <User size={14} style={{ color: "var(--v2-muted)" }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-medium truncate">{v.nome}</div>
                                    <div className="text-[11px] truncate" style={{ color: "var(--v2-muted)" }}>
                                        {v.telefone} · {v.localidade || v.cidade || "—"}
                                    </div>
                                </div>
                                <div className="shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-1 rounded" style={{ background: "var(--v2-primary)15", color: "var(--v2-primary)" }}>
                                    <MessageCircle size={10} /> Abrir
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </motion.div>
        </div>
    );
}
